import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';

import ChatMessage from '../ui/ChatMessage';
import ChatInput from '../ui/ChatInput';
import Toggle from '../ui/Toggle';
import Badge from '../ui/Badge';

const Demo: React.FC = () => {
  const [messages, setMessages] = useState([
    { id: 1, message: "Hey there! Welcome to our design system showcase.", sender: 'bot' as const, timestamp: '2:30 PM' },
    { id: 2, message: "This looks amazing! I love the new color scheme.", sender: 'user' as const, timestamp: '2:31 PM' },
    { id: 3, message: "Thanks! We've redesigned everything with a warmer, more sophisticated palette.", sender: 'bot' as const, timestamp: '2:31 PM' },
  ]);

  const [darkMode, setDarkMode] = useState(false); // Start in light mode
  const [notifications, setNotifications] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [activeTab, setActiveTab] = useState('components');

  // Unified card styling - no more separate dark/light logic
  const cardClasses = 'demo-card rounded-2xl p-6 shadow-lg relative overflow-hidden backdrop-blur-sm border';

  // Unified text classes - using CSS custom properties
  const textClasses = {
    heading: 'text-demo-heading',
    subtext: 'text-demo-subtext',
    label: 'text-demo-label',
    footer: 'text-demo-footer',
  };

  const handleSendMessage = (message: string) => {
    const newMessage = {
      id: messages.length + 1,
      message,
      sender: 'user' as const,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages(prev => [...prev, newMessage]);

    // Simulate bot typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const botResponse = {
        id: messages.length + 2,
        message: "That's a great point! Our design system is built for flexibility and user experience.",
        sender: 'bot' as const,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages(prev => [...prev, botResponse]);
    }, 2000);
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden ${darkMode ? 'bg-dark-900' : 'bg-gray-50'}`}
      style={{
        '--demo-card-bg': darkMode ? 'rgba(39, 39, 42, 0.5)' : 'rgba(255, 255, 255, 0.8)',
        '--demo-card-border': darkMode ? 'rgb(82, 82, 91)' : 'rgb(229, 231, 235)',
        '--demo-text-heading': darkMode ? 'rgb(255, 255, 255)' : 'rgb(17, 24, 39)',
        '--demo-text-subtext': darkMode ? 'rgb(161, 161, 170)' : 'rgb(75, 85, 99)',
        '--demo-text-label': darkMode ? 'rgb(161, 161, 170)' : 'rgb(107, 114, 128)',
        '--demo-text-footer': darkMode ? 'rgb(113, 113, 122)' : 'rgb(107, 114, 128)',
      } as React.CSSProperties}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${darkMode ? 'bg-accent-purple/10' : 'bg-accent-purple/5'}`}></div>
        <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${darkMode ? 'bg-accent-pink/10' : 'bg-accent-pink/5'}`}></div>
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl ${darkMode ? 'bg-accent-magenta/5' : 'bg-accent-magenta/3'}`}></div>
      </div>

      <div className="relative z-10 p-6">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-12"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-3 mb-6"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-accent-purple to-accent-pink flex items-center justify-center shadow-2xl">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <div className="text-left">
                <h1 className={`text-5xl font-display font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Design System
                </h1>
                <div className="text-2xl font-display font-semibold gradient-text">
                  Modern UI Showcase
                </div>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={`text-xl max-w-2xl mx-auto leading-relaxed ${darkMode ? 'text-dark-300' : 'text-gray-600'}`}
            >
              Interactive components and chat elements built for premium applications
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center gap-4 mt-6"
            >
              <Badge variant="success" className="text-sm px-4 py-2">
                <span className="w-2 h-2 bg-accent-purple rounded-full mr-2 animate-pulse"></span>
                Live Demo
              </Badge>
              <Badge variant="info" className="text-sm px-4 py-2">v2.0.1</Badge>
              <Badge variant="warning" className="text-sm px-4 py-2">Beta</Badge>
            </motion.div>
          </div>

          {/* Modern Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className={`rounded-2xl p-2 shadow-lg ${darkMode ? 'glass-effect border border-dark-600' : 'bg-white/80 backdrop-blur-sm border border-gray-200'}`}>
              <div className="flex space-x-2">
                {[
                  { id: 'components', label: 'Components', icon: '🎨' },
                  { id: 'chat', label: 'Chat Demo', icon: '💬' },
                  { id: 'colors', label: 'Colors', icon: '🌈' },
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${activeTab === tab.id
                        ? 'bg-gradient-to-r from-accent-purple to-accent-pink text-white shadow-lg'
                        : darkMode
                          ? 'text-dark-300 hover:text-white hover:bg-dark-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content Sections */}
        <div className="max-w-7xl mx-auto">
          {activeTab === 'components' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              {/* Buttons Showcase */}
              <div className={cardClasses}>
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br rounded-full blur-2xl ${darkMode ? 'from-accent-purple/20 to-transparent' : 'from-accent-purple/10 to-transparent'}`}></div>
                <div className="relative">
                  <h3 className={`text-2xl font-display font-bold mb-2 ${textClasses.heading}`}>
                    Button Components
                  </h3>
                  <p className={`mb-6 ${textClasses.subtext}`}>Interactive buttons with hover effects and animations</p>

                  <div className="space-y-6">
                    <div>
                      <h4 className={`text-sm font-semibold mb-3 uppercase tracking-wider ${textClasses.label}`}>Variants</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <Button variant="primary">Primary</Button>
                        <Button variant="secondary">Secondary</Button>
                        <Button variant="gradient">Gradient</Button>
                        <Button variant="ghost">Ghost</Button>
                      </div>
                    </div>

                    <div>
                      <h4 className={`text-sm font-semibold mb-3 uppercase tracking-wider ${textClasses.label}`}>Sizes</h4>
                      <div className="flex gap-4 items-center">
                        <Button size="sm">Small</Button>
                        <Button size="md">Medium</Button>
                        <Button size="lg">Large</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Elements */}
              <div className={cardClasses}>
                <div className={`absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr rounded-full blur-2xl ${darkMode ? 'from-accent-pink/20 to-transparent' : 'from-accent-pink/10 to-transparent'}`}></div>
                <div className="relative">
                  <h3 className={`text-2xl font-display font-bold mb-2 ${textClasses.heading}`}>
                    Interactive Elements
                  </h3>
                  <p className={`mb-6 ${textClasses.subtext}`}>Toggles, badges, and other interactive components</p>

                  <div className="space-y-6">
                    <div>
                      <h4 className={`text-sm font-semibold mb-3 uppercase tracking-wider ${textClasses.label}`}>Toggles</h4>
                      <div className="space-y-4">
                        <Toggle
                          checked={darkMode}
                          onChange={setDarkMode}
                          label="Dark Mode Theme"
                          isDarkModeToggle={true}
                        />
                        <Toggle
                          checked={notifications}
                          onChange={setNotifications}
                          label="Push Notifications"
                          size="lg"
                        />
                      </div>
                    </div>

                    <div>
                      <h4 className={`text-sm font-semibold mb-3 uppercase tracking-wider ${textClasses.label}`}>Status Badges</h4>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="success">Online</Badge>
                        <Badge variant="warning">Pending</Badge>
                        <Badge variant="error">Offline</Badge>
                        <Badge variant="info">Beta</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'chat' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <div className={cardClasses}>
                <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl rounded-full blur-3xl ${darkMode ? 'from-accent-purple/20 to-transparent' : 'from-accent-purple/10 to-transparent'}`}></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h3 className={`text-3xl font-display font-bold mb-2 ${textClasses.heading}`}>
                        Live Chat Interface
                      </h3>
                      <p className={textClasses.subtext}>Real-time messaging with typing indicators and animations</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-accent-purple rounded-full animate-pulse"></div>
                      <Badge variant="success">Connected</Badge>
                    </div>
                  </div>

                  {/* Chat Container */}
                  <div className={`rounded-2xl p-6 ${darkMode ? 'bg-dark-800/50 border border-dark-600' : 'bg-gray-100/50 border border-gray-300'}`}>
                    <div className="h-96 overflow-y-auto mb-6 space-y-4">
                      {messages.map((msg) => (
                        <ChatMessage
                          key={msg.id}
                          message={msg.message}
                          sender={msg.sender}
                          timestamp={msg.timestamp}
                        />
                      ))}

                      {isTyping && (
                        <ChatMessage
                          message=""
                          sender="bot"
                          isTyping={true}
                        />
                      )}
                    </div>

                    <ChatInput
                      onSendMessage={handleSendMessage}
                      placeholder="Type your message here..."
                      disabled={isTyping}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'colors' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-5xl mx-auto"
            >
              <div className={cardClasses}>
                <div className={`absolute inset-0 bg-gradient-to-br ${darkMode ? 'from-accent-purple/5 via-accent-magenta/5 to-accent-pink/5' : 'from-accent-purple/3 via-accent-magenta/3 to-accent-pink/3'}`}></div>
                <div className="relative">
                  <div className="text-center mb-8">
                    <h3 className={`text-3xl font-display font-bold mb-2 ${textClasses.heading}`}>
                      Color Palette
                    </h3>
                    <p className={textClasses.subtext}>Carefully crafted colors for modern interfaces</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                    {[
                      { name: 'Purple', color: 'bg-accent-purple', hex: '#8b5cf6' },
                      { name: 'Magenta', color: 'bg-accent-magenta', hex: '#d946ef' },
                      { name: 'Pink', color: 'bg-accent-pink', hex: '#ec4899' },
                      { name: 'Fuchsia', color: 'bg-accent-fuchsia', hex: '#c026d3' },
                      { name: 'Rose', color: 'bg-accent-rose', hex: '#f472b6' },
                      { name: 'Primary', color: 'bg-accent-primary', hex: '#e91e63' },
                    ].map((colorItem, index) => (
                      <motion.div
                        key={colorItem.name}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="text-center group cursor-pointer"
                      >
                        <div className={`w-20 h-20 ${colorItem.color} rounded-2xl mx-auto mb-3 shadow-lg group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300`}></div>
                        <h4 className={`font-semibold mb-1 ${textClasses.heading}`}>{colorItem.name}</h4>
                        <p className={`text-xs font-mono ${textClasses.label}`}>{colorItem.hex}</p>
                      </motion.div>
                    ))}
                  </div>

                  <div className={`mt-12 p-6 rounded-2xl ${darkMode ? 'bg-dark-800/50 border border-dark-600' : 'bg-gray-100/50 border border-gray-300'}`}>
                    <h4 className={`text-xl font-display font-semibold mb-4 ${textClasses.heading}`}>Gradient Examples</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="h-16 bg-gradient-to-r from-accent-purple to-accent-pink rounded-xl"></div>
                      <div className="h-16 bg-gradient-to-r from-accent-magenta to-accent-fuchsia rounded-xl"></div>
                      <div className="h-16 bg-gradient-to-r from-accent-rose to-accent-primary rounded-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Modern Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className={`max-w-7xl mx-auto mt-16 pt-8 ${darkMode ? 'border-t border-dark-700/50' : 'border-t border-gray-300/50'}`}
        >
          <div className="text-center">
            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent-purple rounded-full animate-pulse"></div>
                <span className={`text-sm ${textClasses.footer}`}>System Status: Operational</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent-pink rounded-full"></div>
                <span className={`text-sm ${textClasses.footer}`}>Last Updated: Just now</span>
              </div>
            </div>
            <p className={`text-sm ${textClasses.footer}`}>
              Built with React, TypeScript, Tailwind CSS, and Framer Motion • Design System v2.0.1
            </p>
          </div>
        </motion.footer>
      </div>
    </div>
  );
};

export default Demo;